import qs, { type ParsedQuery } from '@common.js/query-string';
import isEmpty from 'lodash/isEmpty';

import { isLucidMarketingDomain } from '../validators/regex';
import isBrowser from '../isBrowser';
import withCookie from '../cookies/withCookie';
import isAbsoluteURL from '../guards/isAbsoluteURL';

export type CustomURLParams = {
  [key: string]: string;
};

type WithURLParamsOptions = {
  customParams?: CustomURLParams[];
  cookies?: string[];
  willAddBeaconParams?: boolean;
};

type SimpleURL = {
  hash?: string;
  host?: string;
  hostname?: string;
  href?: string;
  origin?: string;
  password?: string;
  pathname?: string;
  port?: string;
  protocol?: string;
  search?: string;
};

const withURLParams = (url?: string, options?: WithURLParamsOptions) => {
  if (!url) return '';

  let newUrlObj = {} as SimpleURL;
  let params = {} as CustomURLParams;
  let searchURLParams = {};
  let malformedURLParams = {} as ParsedQuery;
  let malformedURLFragment = '';
  let sessionDataRaw: string | undefined = '{"id": "undefined", "lastUpdatedDate": "undefined"}';
  let anonymousId: string | undefined = 'undefined';

  const { customParams, cookies, willAddBeaconParams = false } = options || {};

  const parsedURL = qs.parseUrl(url, { parseFragmentIdentifier: true });

  if (isAbsoluteURL(url)) {
    newUrlObj = new URL(url);

    if (
      newUrlObj?.origin?.match(/(\/|\.)lucid\.app/gim) ||
      (newUrlObj?.origin?.match(isLucidMarketingDomain) && willAddBeaconParams)
    ) {
      // grab both cookies to add to url query params
      if (isBrowser()) {
        sessionDataRaw = withCookie.get('lt-session-data');
        anonymousId = withCookie.get('lt-anonymous-id');
      }

      // set sessionData params if it exists
      if (sessionDataRaw) {
        try {
          const sessionData: { [key: string]: string } = JSON.parse(sessionDataRaw);

          if (sessionData) {
            const sessionId = sessionData['id'];
            const sessionDate = sessionData['lastUpdatedDate'];

            if (sessionId) {
              params = {
                ...params,
                sessionId,
              };
            }

            if (sessionDate) {
              params = {
                ...params,
                sessionDate,
              };
            }
          }
        } catch (e) {
          console.info('[INFO]: Cookie Sync Params Error - ', e);
        }
      }

      // set anonId param if it exists
      if (anonymousId) {
        params = { ...params, anonId: anonymousId };
      }
    }
  }

  if (parsedURL?.fragmentIdentifier && parsedURL.fragmentIdentifier.includes('?')) {
    const [fragmentIdentifier, fragmentParams] = parsedURL.fragmentIdentifier.split('?');

    malformedURLFragment = fragmentIdentifier;
    malformedURLParams = qs.parse(fragmentParams);
  }

  if (isBrowser()) {
    const { search } = window.location;
    const urlQueryParams = qs.parse(search);

    if (urlQueryParams['s']) {
      delete urlQueryParams['s'];
    }
    searchURLParams = { ...parsedURL?.query, ...urlQueryParams };
  }

  const formatURLParams = customParams?.reduce((previous, currentParam) => {
    const [currentParamKey] = Object.keys(currentParam);

    return {
      ...previous,
      [currentParamKey]: currentParam[currentParamKey],
    };
  }, {});

  const urlCookies = cookies?.reduce((previous, cookie) => {
    const cookieValue = withCookie.get(cookie);

    if (!cookieValue) return previous;

    return {
      ...previous,
      [cookie]: withCookie.get(cookie),
    };
  }, {});

  const parsedURLParams = {
    ...(parsedURL?.query || {}),
    ...malformedURLParams,
    ...formatURLParams,
    ...searchURLParams,
    ...urlCookies,
    ...params,
  };

  const urlFragmentIdentifier = malformedURLFragment || parsedURL?.fragmentIdentifier;

  return `${parsedURL?.url}${!isEmpty(parsedURLParams) ? `?${qs.stringify(parsedURLParams)}` : ''}${
    urlFragmentIdentifier ? `#${urlFragmentIdentifier}` : ''
  }`;
};

export default withURLParams;
